import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, fmtMoney, extractErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { CreditCard, Loader2 } from "lucide-react";

// Customer-facing payment box. Stripe is the active payment method. The PayPal backend integration
// (endpoints, config, capture/webhook logic, records, dependency) is intentionally preserved and can
// be re-surfaced here later without rebuilding it. Reuses the existing Stripe checkout/webhook,
// authorization, idempotency, compensation and audit logic — no new competing billing system.
export function PayInvoiceButton({ invoice, onPaid }) {
  const [stripe, setStripe] = useState(null);
  const [open, setOpen] = useState(false);
  const [working, setWorking] = useState(false);
  const [method, setMethod] = useState(null);
  useEffect(() => {
    api.get("/stripe/config").then(({ data }) => setStripe(data)).catch(() => setStripe({ configured: false }));
  }, []);
  if (!invoice || invoice.payment_status === "paid" || (invoice.balance_due || 0) <= 0) return null;
  if (!stripe?.configured) return null;

  const startStripe = async (win) => {
    setWorking(true);
    try {
      const { data } = await api.post(`/invoices/${invoice.id}/stripe/checkout`, { origin_url: window.location.origin });
      const url = data && data.checkout_url;
      if (!url || !/^https?:\/\//.test(url)) throw new Error("no-checkout-url");
      // Stripe Checkout + hCaptcha must run TOP-LEVEL. The preview renders the app in an iframe,
      // so we open Checkout in a new browser tab (window pre-opened on the click gesture to dodge
      // popup blockers). Falls back to a same-tab top navigation if the popup was blocked.
      if (win && !win.closed) { win.location = url; }
      else if (!window.open(url, "_blank")) { (window.top || window).location.href = url; }
      setWorking(false);
      setMethod(null);
      setOpen(false);
      toast.message("Stripe checkout opened in a new tab — complete payment there, then return to this tab.");
    } catch (e) {
      if (win && !win.closed) { try { win.close(); } catch (_) { /* noop */ } }
      setWorking(false);
      setMethod(null);
      toast.error(extractErrorMessage(e, "Could not start Stripe checkout. Please try again."));
    }
  };

  return (
    <>
      <Button data-testid={`pay-now-${invoice.invoice_number}`} className="mt-3 w-full bg-emerald-600 text-white hover:bg-emerald-700" onClick={() => { setMethod(null); setOpen(true); }}>
        <CreditCard className="mr-1.5 h-4 w-4" /> Pay now · {fmtMoney(invoice.currency, invoice.balance_due)}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="pay-invoice-dialog">
          <DialogHeader>
            <DialogTitle>Pay invoice {invoice.invoice_number}</DialogTitle>
            <DialogDescription>
              {invoice.case_number ? `Case ${invoice.case_number} · ` : ""}Job #{invoice.job_number} — securely pay the outstanding balance.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm">
              <div className="flex justify-between"><span className="text-slate-500">Amount due</span><span data-testid="pay-amount-due" className="font-semibold text-slate-900">{fmtMoney(invoice.currency, invoice.balance_due)}</span></div>
            </div>

            {method !== "stripe" && (
              <div className="space-y-2" data-testid="pay-method-choice">
                <Button data-testid="choose-stripe" disabled={working} className="w-full bg-indigo-600 text-white hover:bg-indigo-700" onClick={() => { const win = window.open("", "_blank"); setMethod("stripe"); startStripe(win); }}>
                  {working ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <CreditCard className="mr-1.5 h-4 w-4" />} Pay with card (Stripe)
                </Button>
              </div>
            )}

            {method === "stripe" && (
              <div className="py-4 text-center text-sm text-slate-500" data-testid="stripe-redirecting">
                <Loader2 className="mx-auto mb-2 h-5 w-5 animate-spin" /> Opening secure Stripe checkout in a new tab…
              </div>
            )}

            <p className="text-center text-xs text-slate-400">
              {stripe?.mode === "test" ? "Test mode — no real charge" : "Secured payment"}
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
